// Authored content for the app-idea lesson — the opening lesson of every project
// course (CD02's V2101, CD03's V3101, CD04's V4101), the ones the registry marks
// with `captureProject: true`.
//
// WHY THIS IS AUTHORED RATHER THAN GENERATED
//
// This lesson is graded on the "My app idea" card, not on the chat. A generated
// rubric can't be: it comes back worded for a transcript ("the student engages in
// back-and-forth conversation with Claude to refine their idea"), so a student who
// filled the card in perfectly and never opened the chat failed the lesson — and a
// student who chatted but never saved the card passed a lesson whose entire point
// is the saved card. See MyAppIdeaCard: that record is what personalizes every
// later lesson.
//
// It is also DELIBERATELY LENIENT. This lesson only has to get a name, a sentence
// and an audience out of the student; the app's real detail — features, data,
// screens, what's out of scope — is settled later in "Turning your idea into a
// build spec with Claude" (V2401 / V3401 / V4401, see buildSpecLesson.js), which
// grades exactly those things and pre-credits whatever this lesson produced.
// Grading depth here would be asking for the same work twice, at the point in the
// course where a beginner is most likely to quit.
//
// The hub overrides `definition.rubric`, `exercisePrompt` and `hint` on read — the
// same precedent as buildSpecLesson.js and the authored canvases. Overriding on
// read means no cache purge and no DEF_VERSION bump, so students keep their cached
// prose. `lessonMarkdown` is deliberately NOT overridden: it teaches how to pick an
// idea, which is unchanged, and its pregenerated ElevenLabs narration is keyed to
// that exact text.
//
// The rubric is graded server-side against the SAVED CARD, not the transcript —
// see `gradeProject` in RobotAIServices.js and buildIdeaCardEvidence in
// backend/src/services/ClaudeCourseService.js. Three criteria is not an accident:
// the judge passes a lesson at 80%, so three means all three must pass, and each
// one maps to a field the card refuses to save without.

/** The three fields the rubric grades, in card order. Documentation for the
 *  reader — the criteria below are written in this order and MyAppIdeaCard blocks
 *  saving until all three are filled in, so a saved card always has something to
 *  grade on every line. */
export const APP_IDEA_FIELDS = ["appName", "whatItDoes", "whoItsFor"];

const COPY = {
    en: {
        rubric: [
            "Your idea card gives the app a name.",
            "It says what the app does, in your own words.",
            "It says who the app is for — even if that's just you.",
        ],
        exercise: [
            "Fill in the **My app idea** panel: what you'd call it, what it does, and who it's for. Two or three sentences is plenty.",
            "",
            "Not sure yet? Talk it through with Claude in the chat under the rubric — it's there to help you think, and it is not graded.",
            "",
            "Then press **Save & submit for grading** — one button, it saves your idea and grades it. Don't worry about getting every detail right: you'll turn this into a full plan later, in \"Turning your idea into a build spec with Claude\".",
        ].join("\n"),
        hint:
            "Pick something small on purpose — one thing, done well, for one kind of person. \"A page that lists this week's pickup games\" is a first app; \"a social network for sports\" is not.\n\nIf nothing comes to mind, paste this to Claude: **\"I want to build my first small app. Ask me three questions about my life, then suggest something small I could actually finish.\"**",
    },
    es: {
        rubric: [
            "Tu tarjeta de idea le pone un nombre a la app.",
            "Dice qué hace la app, con tus propias palabras.",
            "Dice para quién es la app — aunque seas solo tú.",
        ],
        exercise: [
            "Llena el panel **Mi idea de app**: cómo la llamarías, qué hace y para quién es. Con dos o tres frases basta.",
            "",
            "¿Todavía no lo tienes claro? Coméntalo con Claude en el chat que está debajo de la rúbrica — está ahí para ayudarte a pensar y no se califica.",
            "",
            "Después pulsa **Guardar y enviar para calificar** — un solo botón: guarda tu idea y la califica. No te preocupes por acertar en cada detalle: más adelante lo convertirás en un plan completo, en \"Convertir tu idea en un plan de construcción con Claude\".",
        ].join("\n"),
        hint:
            "Elige algo pequeño a propósito — una sola cosa, bien hecha, para un solo tipo de persona. \"Una página con los partidos de esta semana\" es una primera app; \"una red social de deportes\" no.\n\nSi no se te ocurre nada, pégale esto a Claude: **\"Quiero construir mi primera app pequeña. Hazme tres preguntas sobre mi vida y luego propón algo pequeño que pueda terminar de verdad.\"**",
    },
};

const copyFor = (locale) => (locale === "es" ? "es" : "en");

/** The lesson's rubric: one criterion per graded card field, in card order. */
export const appIdeaRubric = (locale) => [...COPY[copyFor(locale)].rubric];

/** The exercise prompt and hint shown in the lesson pane. */
export const appIdeaExercise = (locale) => {
    const copy = COPY[copyFor(locale)];
    return { exercisePrompt: copy.exercise, hint: copy.hint };
};
